import { memo, useContext, useMemo } from 'react'
import type { SubAppModuleFederation } from '@ucc/admin-module-federation-utils'
import { useVariant } from '@unleash/proxy-client-react'

import config from 'config'
import { branchNameSubfolder } from 'config/featureBranch'
import { SubAppRouteConfig } from 'types/SubAppRouteConfig'
import { joinUrl } from 'utils'
import { UNLEASH_FEATURE_FLAGS } from 'utils/constants'
import ModuleFederationMount from 'utils/ModuleFederationMount'
import AppsVersionContext from '../contexts/AppsVersionContext'

const buildInboxConfig = (dceVersion: string) => {
  const wcLoaderTargetBuild = branchNameSubfolder.length > 0 ? branchNameSubfolder : JSON.parse(dceVersion)['wc-loader']
  const wcLoaderTargetBuildOverride = localStorage.getItem(UNLEASH_FEATURE_FLAGS.dceVersion)

  return {
    uccUnifiedAdminInboxConfiguration: {
      ...config.subAppsModuleFederation.uccUnifiedAdminInbox,
      url: joinUrl(
        config.subAppsModuleFederation.uccUnifiedAdminInbox.url,
        wcLoaderTargetBuildOverride ? JSON.parse(wcLoaderTargetBuildOverride)['wc-loader'] : wcLoaderTargetBuild,
      ),
    },
  }
}

const buildCCConfigurationConfig = (contactCenterTargetBuild: string) => {
  return {
    uccUnifiedAdminCCConfiguration: {
      ...config.subAppsModuleFederation.uccUnifiedAdminCCConfiguration,
      url: joinUrl(config.subAppsModuleFederation.uccUnifiedAdminCCConfiguration.url, contactCenterTargetBuild),
    },
  }
}

type Module = 'settings' | 'inbox-settings'

const RouteModuleMap: Map<string, Module> = new Map([
  ['customer-engagement', 'settings'],
  ['inboxes', 'inbox-settings'],
])

const getModule = (route: string): Module => {
  const routeParts = route.split('/')
  const endPart = routeParts[routeParts.length - 1]
  return RouteModuleMap.get(endPart)!
}

type InboxConfigurationRoutesComponentProps = {
  module: Module
  uccUnifiedAdminInboxConfiguration: SubAppModuleFederation
}

type CCConfigurationRoutesComponentProps = {
  module: Module
  uccUnifiedAdminCCConfiguration: SubAppModuleFederation
}

const InboxRoutesComponent = memo(
  ({ module, uccUnifiedAdminInboxConfiguration }: InboxConfigurationRoutesComponentProps) => {
    return (
      <ModuleFederationMount
        module={module}
        subAppModuleFederation={uccUnifiedAdminInboxConfiguration}
        key={module}
        initSentry={false}
      />
    )
  },
)

const CCConfigurationRoutesComponent = memo(
  ({ module, uccUnifiedAdminCCConfiguration }: CCConfigurationRoutesComponentProps) => {
    return module === RouteModuleMap.get('inboxes') ? (
      <ModuleFederationMount
        module={'ccConfigurationAppInvictus'}
        subAppModuleFederation={uccUnifiedAdminCCConfiguration}
        key="ccConfigurationAppInvictus"
        initSentry={false}
      />
    ) : null
  },
)

const useInboxRoutes = (path: string): SubAppRouteConfig[] => {
  const { contactCenterTargetBuild } = useContext(AppsVersionContext)
  const dceVersion = useVariant(UNLEASH_FEATURE_FLAGS.dceVersion)?.payload

  // Using `useMemo` here as the web components getting mounted through the different
  // inbox routes require not unmounting when moving from one internal route to another.
  // Failing to do so causes the web components to get completely unmounted and thus
  // fully reloaded on each sub-route change.
  return useMemo(() => {
    if (!dceVersion) {
      return []
    }
    const { uccUnifiedAdminInboxConfiguration } = buildInboxConfig(dceVersion.value)
    const { uccUnifiedAdminCCConfiguration } = buildCCConfigurationConfig(contactCenterTargetBuild)
    return (
      config.subAppsModuleFederation.uccUnifiedAdminInbox.routes?.map((route) => ({
        paths: [`${path}/${route}`],
        component: () => (
          <>
            <InboxRoutesComponent
              module={getModule(route)}
              uccUnifiedAdminInboxConfiguration={uccUnifiedAdminInboxConfiguration}
            />
            <CCConfigurationRoutesComponent
              module={getModule(route)}
              uccUnifiedAdminCCConfiguration={uccUnifiedAdminCCConfiguration}
            />
          </>
        ),
      })) ?? []
    )
  }, [path, dceVersion, contactCenterTargetBuild])
}

export default useInboxRoutes
